import React, { useEffect, useState, useCallback, useMemo } from "react";
import { supabase } from "../../supabase";
import Layout from "../../components/admin/Layout";
import AdminList from "../../components/admin/AdminList";
import IconTextButton from "../../components/admin/IconTextButton";
import Modal from "../../components/admin/FormModal";
import Input from "../../components/admin/Input";
import { Map, MapMarker, MarkerClusterer } from "react-kakao-maps-sdk";
import styles from "./Stadium.module.css";

function Stadium() {
  const [stadiums, setStadiums] = useState([]);
  const [filteredStadiums, setFilteredStadiums] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState("map");
  const [editingStadium, setEditingStadium] = useState(null);
  const [mapCenter, setMapCenter] = useState({ lat: 37.5665, lng: 126.978 });
  const [zoomLevel, setZoomLevel] = useState(8);
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    address: "",
    address_detail: "",
    lat: "",
    lng: "",
  });
  const [filter, setFilter] = useState('social'); // 기본값을 'social'로 변경

  const MAX_ZOOM_FOR_MARKERS = 6; // 마커를 렌더링할 최대 줌 레벨
  const ROWS_PER_PAGE = 1000; // 한 번에 가져올 레코드 수

  // 모든 데이터를 가져오기 (페이징)
  const fetchAllStadiums = useCallback(async () => {
    setIsLoading(true);
    try {
      let allData = [];
      let page = 0;
      let fetchedData;

      do {
        const { data, error } = await supabase
          .from("stadium")
          .select("*")
          .range(page * ROWS_PER_PAGE, (page + 1) * ROWS_PER_PAGE - 1)
          .order("name");

        if (error) throw error;

        fetchedData = data;
        allData = [...allData, ...data]; // 데이터 누적
        page += 1; // 다음 페이지로 이동
      } while (fetchedData && fetchedData.length === ROWS_PER_PAGE); // 마지막 페이지에 도달할 때까지 반복

      setStadiums(allData);
      setFilteredStadiums(allData);
    } catch (error) {
      console.error("Error fetching all stadiums:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllStadiums(); // 페이지 로드 시 모든 데이터 가져오기
  }, [fetchAllStadiums]);

  // 필터링 함수
  const filterStadiums = useCallback((stadiums) => {
    switch(filter) {
      case 'social':
        return stadiums.filter(stadium => stadium.is_social === 1);
      case 'nonsocial':
        return stadiums.filter(stadium => stadium.is_social === 0);
      default:
        return stadiums;
    }
  }, [filter]);


  // 필터 변경 시 재필터링
  useEffect(() => {
    if (stadiums.length > 0) {
      setFilteredStadiums(filterStadiums(stadiums));
    }
  }, [filter, stadiums, filterStadiums]);

  // 지도 영역 변경 시 필터링
  const handleBoundsChange = (map) => {
    const bounds = map.getBounds();
    if (!bounds) return;

    const boundsFiltered = stadiums.filter((stadium) => {
      const lat = parseFloat(stadium.lat);
      const lng = parseFloat(stadium.lng);
      return (
        lat >= bounds.getSouthWest().getLat() &&
        lat <= bounds.getNorthEast().getLat() &&
        lng >= bounds.getSouthWest().getLng() &&
        lng <= bounds.getNorthEast().getLng()
      );
    });
    
    setFilteredStadiums(filterStadiums(boundsFiltered));
  };

  const handleZoomChange = (map) => {
    const level = map.getLevel();
    setZoomLevel(level);
    console.log("Current zoom level:", level);
  };

  // 모달 관련 함수들
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  const openModal = useCallback((stadium = null) => {
    setEditingStadium(stadium);
    setForm(stadium ? {
      name: stadium.name,
      address: stadium.address,
      address_detail: stadium.address_detail || '',
      lat: stadium.lat,
      lng: stadium.lng,
      is_social: stadium.is_social,
    } : {
      name: "",
      address: "",
      address_detail: "",
      lat: "",
      lng: "",
      is_social: "",
    });
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingStadium(null);
    setForm({
      name: "",
      address: "",
      address_detail: "",
      lat: "",
      lng: "",
      is_social: "",
    });
  }, []);

  const handleSubmit = useCallback(
    async (form) => {
      try {
        if (editingStadium) {
          await supabase.from("stadium").update(form).eq("id", editingStadium.id);
        } else {
          await supabase.from("stadium").insert([form]);
        }
        await fetchAllStadiums();
        closeModal();
      } catch (error) {
        console.error("Error saving stadium:", error);
      }
    },
    [editingStadium, fetchAllStadiums, closeModal]
  );

  const headers = [
    { label: "구장명", key: "name" },
    { label: "주소", key: "address" },
    { label: "위도", key: "lat" },
    { label: "경도", key: "lng" },
    { label: "소셜", key: "is_social" },
  ];

  return (
    <Layout>
      <div className={styles.container}>
        <div className={styles.toolBar}>
          <div className={styles.leftButtons}>
            <IconTextButton
              icon="icon_plus_square.svg"
              text="추가하기"
              onClick={() => openModal()}
              state="Enabled"
            />
          </div>
          <div className={styles.centerButtons}>
            <IconTextButton
              text="전체"
              onClick={() => setFilter('all')}
              state={filter === 'all' ? 'Selected' : 'Enabled'}
            />
            <IconTextButton
              text="소셜"
              onClick={() => setFilter('social')}
              state={filter === 'social' ? 'Selected' : 'Enabled'}
            />
            <IconTextButton
              text="일반"
              onClick={() => setFilter('nonsocial')}
              state={filter === 'nonsocial' ? 'Selected' : 'Enabled'}
            />
          </div>
          <div className={styles.rightButtons}>
            <IconTextButton
              icon="icon_map.svg"
              text="지도"
              onClick={() => setViewMode("map")}
              state={viewMode === "map" ? "Selected" : "Enabled"}
            />
            <IconTextButton
              icon="icon_list.svg"
              text="목록"
              onClick={() => setViewMode("list")}
              state={viewMode === "list" ? "Selected" : "Enabled"}
            />
          </div>
        </div>

        {viewMode === "list" ? (
          <AdminList data={stadiums} headers={headers} onRowClick={openModal} />
        ) : (
          <div className={styles.mapContainer}>
            <Map
              center={mapCenter}
              style={{ width: "100%", height: "100%" }}
              level={zoomLevel}
              onBoundsChanged={handleBoundsChange}
              onZoomChanged={handleZoomChange}
            >
              {zoomLevel > MAX_ZOOM_FOR_MARKERS ? (
                <MarkerClusterer averageCenter={true} minLevel={6}>
                  {filteredStadiums.map((stadium) => (
                    <MapMarker
                      key={stadium.id}
                      position={{
                        lat: parseFloat(stadium.lat),
                        lng: parseFloat(stadium.lng),
                      }}
                      onClick={() => openModal(stadium)}
                    />
                  ))}
                </MarkerClusterer>
              ) : (
                filteredStadiums.map((stadium) => (
                  <MapMarker
                    key={stadium.id}
                    position={{
                      lat: parseFloat(stadium.lat),
                      lng: parseFloat(stadium.lng),
                    }}
                    onClick={() => openModal(stadium)}
                  >
                    <div className={styles.markerContent} onClick={() => openModal(stadium)}>{stadium.name}</div>
                  </MapMarker>
                ))
              )}
            </Map>
            {isLoading && (
              <div className={styles.loadingOverlay}>
                <div className={styles.loadingDots}>
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}
          </div>
        )}

        <Modal
          title={editingStadium ? "구장 수정" : "구장 추가"}
          isOpen={isModalOpen}
          onClose={closeModal}
          onSubmit={handleSubmit}
        >
          <Input
            label="구장명"
            name="name"
            value={form.name}
            onChange={handleChange}
            required
          />
          <Input
            label="주소"
            name="address"
            value={form.address}
            onChange={handleChange}
            required
          />
          <Input
            label="상세주소"
            name="address_detail"
            value={form.address_detail}
            onChange={handleChange}
          />
          <Input
            label="위도"
            name="lat"
            type="number"
            value={form.lat}
            onChange={handleChange}
            required
          />
          <Input
            label="경도"
            name="lng"
            type="number"
            value={form.lng}
            onChange={handleChange}
            required
          />
          <Input
            label="소셜"
            name="is_social"
            value={form.is_social}
            onChange={handleChange}
          />
        </Modal>
      </div>
    </Layout>
  );
}

export default Stadium;